import React, { useEffect } from "react";
import cross from "../assets/icons/cross.svg";
import { useState } from "react";
import SignIn from "./SignIn/SignIn";
import SignUp from "./SignUp/SignUp";
import { useTheme } from "../context/ThemeProvider";
import { Link } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import axios from "axios";
import { getVideoPriceRupees } from "../utils/pricing";
import { hasActiveSubscription } from "../utils/subscriptionUtils";

const SubscribeModal = ({ video, setShowModal }) => {
  const { auth } = useAuth();
  const isSubscribed = hasActiveSubscription(auth?.subscription);

  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [masterData, setMasterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { darkMode } = useTheme();

  useEffect(() => {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const bodyStyle = document.body.style;
    const htmlStyle = document.documentElement.style;
    const prevBody = {
      overflow: bodyStyle.overflow,
      position: bodyStyle.position,
      top: bodyStyle.top,
      width: bodyStyle.width,
      paddingRight: bodyStyle.paddingRight,
    };
    const prevHtmlOverflow = htmlStyle.overflow;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    htmlStyle.overflow = "hidden";
    bodyStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.width = "100%";
    if (scrollbarWidth > 0) {
      bodyStyle.paddingRight = `${scrollbarWidth}px`;
    }

    // Fetch master data for pricing
    const fetchMasterData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/config/master-data`,
        );

        if (response.data.success && response.data.data) {
          setMasterData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching master data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMasterData();

    // Allow scroll when modal unmounts
    return () => {
      htmlStyle.overflow = prevHtmlOverflow;
      bodyStyle.overflow = prevBody.overflow;
      bodyStyle.position = prevBody.position;
      bodyStyle.top = prevBody.top;
      bodyStyle.width = prevBody.width;
      bodyStyle.paddingRight = prevBody.paddingRight;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Calculate dynamic pricing values
  const getAnnualPrice = () => {
    return masterData?.subscriptionPlans?.Annual;
  };

  const getAnnualMonthlyRate = () => {
    const annual = getAnnualPrice();
    if (!annual) return null;
    return Math.round(annual / 12);
  };

  const getMinOneTimePrice = () => {
    const prices = masterData?.oneTimePrices;
    if (!prices) return null;
    return getVideoPriceRupees(prices);
  };

  const downloadWatermark = async () => {
    try {
      const headers = auth?.RawToken
        ? { Authorization: `Bearer ${auth.RawToken}` }
        : undefined;

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/downloadwatermark`,
        { id: video._id },
        {
          headers,
          responseType: "blob", // Important for file downloads
        },
      );

      // Create download link from the blob response
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `watermarked_video_${video._id}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      console.error("Download failed:", e);
      // Handle errors (show toast/notification)
    }
  };

  return (
    <div
      id="subscribe-modal"
      className="bg-[#121212CC] h-screen fixed inset-0 z-50 modalOverflow flex items-center"
      onClick={() => setShowModal(false)} // close modal on background click
    >
      <div className="bigscreen max-h-[100vh] p-10">
        <div
          className={`relative max-w-[1024px] max-h-full border p-8 ${
            darkMode
              ? "bg-[#10130D] border-[#1E1E1E]"
              : "bg-white border-[#CBCBCB]"
          } shadow`}
          onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside modal
        >
          <div
            className="cursor-pointer h-8 absolute top-4 right-4"
            onClick={() => setShowModal(false)}
          >
            <img src={cross} alt="close" className="h-full object-contain" />
          </div>

          <div className="p-10 flex flex-col gap-8 justify-center">
            <div className="flex flex-col md:flex-row items-center justify-center md:!justify-evenly gap-6">
              {/* Premium Subscription Section */}
              <div className="w-full max-w-[360px] text-sm text-center flex flex-col items-center justify-evenly gap-2 min-h-[300px]">
                <h1 className="text-3xl font-bold mb-2 whitespace-nowrap">
                  <span>Go </span>
                  <span className="text-[var(--primary)]">Unlimited!</span>
                </h1>

                <div className="flex flex-col items-center mb-4">
                  {loading || !getAnnualMonthlyRate() ? (
                    <div
                      className={`animate-pulse h-6 ${
                        darkMode ? "bg-gray-700" : "bg-gray-200"
                      } rounded w-48`}
                    ></div>
                  ) : !isSubscribed ? (
                    <>
                      <Link to="/pricing">
                        <button className="greenButton">
                          ₹ {getAnnualMonthlyRate()}/ month
                        </button>
                      </Link>
                      <div className="text-[var(--grey)] mt-2 text-sm">
                        (when billed annually)
                      </div>
                    </>
                  ) : null}
                </div>

                <div className="mb-6">
                  <span className="font-semibold">Get full access to all videos</span>
                </div>

                {/* Replaced 'View Plans' with highlighted price button above */}
              </div>

              {!isSubscribed && (
              <>
              {/* Divider */}
              <div className="w-full md:w-px h-px md:h-[260px] bg-white"></div>

              {/* One-time video purchase */}
              <div className="w-full max-w-[360px] text-sm text-center flex flex-col items-center justify-evenly gap-2 min-h-[300px]">
                <h1 className="text-3xl font-bold mb-2 whitespace-nowrap">
                  <span>Get just </span>
                  <span className="text-[var(--primary)]">1 video</span>
                </h1>
                <div className="font-semibold mb-4">
                  {loading || !getMinOneTimePrice() ? (
                    <div
                      className={`animate-pulse h-6 ${
                        darkMode ? "bg-gray-700" : "bg-gray-200"
                      } rounded w-56`}
                    ></div>
                  ) : (
                    <>
                      {auth?.role ? (
                        video.shotonmobile ? (
                          <Link to={`/onetimepurchase/${video?._id}?type=mobile`}>
                            <button className="greenButton">
                              ₹{getMinOneTimePrice()} / video
                            </button>
                          </Link>
                        ) : (
                          <Link to={`/onetimepurchase/${video?._id}?type=highres`}>
                            <button className="greenButton">
                              ₹{getMinOneTimePrice()} / video
                            </button>
                          </Link>
                        )
                      ) : (
                        <button
                          className="greenButton"
                          onClick={() => setShowSignInModal(true)}
                        >
                          ₹{getMinOneTimePrice()} / video
                        </button>
                      )}
                      <div className="text-[var(--grey)] mt-2 text-sm opacity-0">
                        (spacer)
                      </div>
                    </>
                  )}
                </div>

                <div className="mb-6">
                  <span className="font-semibold">Get only what you need</span>
                </div>

                {/* Right side: single highlighted price button above replaces 'Sign In and Pay' and 'Add to Cart' */}
              </div>
              </>
              )}
            </div>

            <p className="text-[var(--grey)] text-sm text-center mx-auto">
              To Download a Watermarked Preview,{"  "}
              <span
                className="cursor-pointer text-[var(--primary)] hover:text-[var(--hover)] transition-all duration-250"
                onClick={() => downloadWatermark(true)}
              >
                Click Here!
              </span>
            </p>
          </div>
        </div>
      </div>

      {showSignInModal && (
        <SignIn
          setShowModal={setShowSignInModal}
          switchToSignUp={() => {
            setShowSignInModal(false);
            setShowSignUpModal(true);
          }}
          redirectAfterLogin={
            video?.shotonmobile
              ? `/onetimepurchase/${video?._id}?type=mobile`
              : `/onetimepurchase/${video?._id}?type=highres`
          }
        />
      )}
      {showSignUpModal && (
        <SignUp
          setShowModal={setShowSignUpModal}
          switchToSignIn={() => {
            setShowSignUpModal(false);
            setShowSignInModal(true);
          }}
          redirectAfterLogin={
            video?.shotonmobile
              ? `/onetimepurchase/${video?._id}?type=mobile`
              : `/onetimepurchase/${video?._id}?type=highres`
          }
        />
      )}
    </div>
  );
};

export default SubscribeModal;
